/**
 * Joining (WALLIYO-0131): who has been invited and has not arrived yet, and
 * who arrived recently.
 *
 * Both come out of the same list the Records tab reads — an invitation is a
 * staff member with `status: "invited"`, not a second kind of record — so this
 * is a way of reading that list rather than another endpoint.
 */

import type { StaffMemberListItem } from "@wallyo/contracts/console";

/**
 * How long somebody counts as newly arrived.
 *
 * Long enough that a joiner is still on the tab after their first fortnight,
 * short enough that it is a list of people to settle in rather than the whole
 * company sorted by date.
 */
export const RECENTLY_JOINED_DAYS = 30;

export type JoiningLists = {
  /** Invited and not yet able to sign in, oldest invitation first. */
  invited: readonly StaffMemberListItem[];
  /** Arrived within the last RECENTLY_JOINED_DAYS, most recent first. */
  arrived: readonly StaffMemberListItem[];
};

const time = (iso: string): number => {
  const value = Date.parse(iso);
  return Number.isNaN(value) ? 0 : value;
};

/**
 * Splits the staff list into the two halves the tab is about.
 *
 * `now` is passed in rather than read, so "recently" is testable and so two
 * renders of the same list never disagree about where the boundary is.
 */
export function joining(staff: readonly StaffMemberListItem[], now: Date): JoiningLists {
  const cutoff = now.getTime() - RECENTLY_JOINED_DAYS * 24 * 60 * 60 * 1000;

  const invited = staff
    .filter((member) => member.status === "invited")
    // Oldest first: an invitation nobody has accepted is the one worth
    // chasing, and it is the one that has been waiting longest.
    .sort((left, right) => time(left.createdAt) - time(right.createdAt));

  const arrived = staff
    .filter((member) => member.status === "active" && time(member.createdAt) >= cutoff)
    .sort((left, right) => time(right.createdAt) - time(left.createdAt));

  return { invited, arrived };
}

/**
 * How long an invitation has been out, in whole days.
 *
 * Whole days because that is the unit the answer is acted on in: an invitation
 * sent this morning and one sent at lunch are both "today", and neither is
 * worth chasing yet.
 */
export function daysWaiting(member: StaffMemberListItem, now: Date): number {
  const days = Math.floor((now.getTime() - time(member.createdAt)) / (24 * 60 * 60 * 1000));
  return days > 0 ? days : 0;
}

/**
 * An invitation worth chasing: out for a week with nobody on the other end.
 *
 * Said on the row rather than counted somewhere, because the thing to do about
 * it is to talk to the person, and that only happens if the row says so.
 */
export const STALE_INVITATION_DAYS = 7;

export function isStale(member: StaffMemberListItem, now: Date): boolean {
  return daysWaiting(member, now) >= STALE_INVITATION_DAYS;
}
